import { useEffect, useState } from 'react';
import { useAcLedMappingPost } from 'actions/ac-led-mapping';
import { searchTeachersImportMapper, studentDetailsImportMapper } from '../helpers';
import { useDispatch, useSelector } from 'react-redux';
import { showNotification } from 'actions/notification';
import { openModal } from 'actions/modal';
import { MODAL_FORM } from 'constants/form';
import {
  AC_LED_MAPPING_MODAL_TYPES_CONFIG,
  AC_LED_MAPPING_FILTERS_KEY,
  AC_MAPPING_CONFIG,
  MAPPER_TYPE,
} from 'constants/ac-led-mapping';
import { store } from 'redux-rest-reducer';
import { AC_LED_MAPPING_EVENTS } from 'constants/ac-led-mapping';
import { formatErrorMessage } from 'utils';
import { get as getConfig } from 'actions/configs';
import { getNewTimeSlot } from 'modals/auto-mapping-modal/helpers';
import moment from 'moment';
import { mappingResultImportMapper } from '../helpers';
const useLogic = () => {
  const [prospectID, setProspectID] = useState('');
  const [offset, setOffset] = useState(0);
  const [teacherPageNumber, setTeacherPageNumber] = useState(1);
  const [filters, setFilters] = useState({});
  const [searchResult, setSearchResult] = useState(null);
  const [viewNextPage, setViewNextPage] = useState(false);
  const [studentDetails, setStudentDetails] = useState(null);
  const [dateTimePreset, setDateTimePreset] = useState([]);
  const [showDateTimeFilter, setShowDateTimeFilter] = useState(false);
  const [mapperType, setMapperType] = useState(MAPPER_TYPE.AC);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getConfig?.(AC_MAPPING_CONFIG));
  }, [dispatch]);
  const configs = useSelector(({ configs }) => configs);
  const { filters: mappingFilters } =
    store.get(configs, AC_LED_MAPPING_FILTERS_KEY)?.data ?? {};
  const { max_pages: maxPages, page_limit: pageLimit } =
    store.get(configs, AC_MAPPING_CONFIG)?.data ?? {};

  const onFetchStudentDetailsComplete = (errorMessage, data) => {
    if (errorMessage) {
      setFilters({});
      setStudentDetails(null);
      dispatch(
        showNotification({
          text: formatErrorMessage(errorMessage),
          duration: 10,
          showCloseIcon: true,
        }),
      );
    } else {
      const updatedFilters = mappingFilters?.reduce((res, { code }) => {
        if (code && data?.[code]) {
          return {
            ...res,
            [code]: data?.[code],
          };
        }
        return { ...res };
      }, {});
      const studentData = studentDetailsImportMapper(data);
      setFilters({ ...updatedFilters });
      setStudentDetails({ ...studentData });
      const mapperType = data?.mapper_type;
      setMapperType(mapperType);
      if (mapperType === MAPPER_TYPE.MAPPER) {
        setShowDateTimeFilter(true);
      }
    }
  };

  const {
    post: fetchStudentDetails,
    isProcessing: isStudentDetailsProcessing,
  } = useAcLedMappingPost({
    onComplete: onFetchStudentDetailsComplete,
  });

  const { post: searchResultFeedbackClick } = useAcLedMappingPost({
    onComplete: () => {},
  });
  const handleSearchComplete = (errorMessage, data) => {
    if (errorMessage) {
      dispatch(
        showNotification({
          text: formatErrorMessage(errorMessage),
          duration: 10,
          showCloseIcon: true,
        }),
      );
    } else {
      const responseData = data?.event_payload?.response;
      if (mapperType === MAPPER_TYPE.AC) {
        const searchData = searchTeachersImportMapper(responseData);
        const { modal } = searchData;
        const { code, title, description, cta } = modal ?? {};

        if (title && description && code) {
          dispatch(
            openModal(
              AC_LED_MAPPING_MODAL_TYPES_CONFIG?.[code]?.modalCode,
              {
                size: 'custom',
                width: '512px',
              },
              {
                title,
                description,
                cta,
              },
            ),
          );
        }
        setSearchResult(searchData);
      } else {
        const mappingSearchResult = mappingResultImportMapper(responseData);
        setSearchResult(mappingSearchResult);

        dispatch(
          openModal(
            MODAL_FORM.MAPPER_MAPPING_MODAL,
            {
              size: 'custom',
              width: '720px',
            },
            { mappingSearchResult, dateTimePreset, filters, prospectId: prospectID },
          ),
        );
      }
    }
  };
  const { post: searchTeachers, isProcessing: isSearchProcessing } = useAcLedMappingPost({
    onComplete: handleSearchComplete,
  });

  const setOffsetAndSearchTeachers = (updatedOffset, updatedTeacherPageNumber) => {
    setOffset(updatedOffset);
    setTeacherPageNumber(updatedTeacherPageNumber);
    handleSearchTeachersWithOffsetAndLimit(updatedOffset);
  };

  const handleNextTeacherPageButton = () => {
    setOffsetAndSearchTeachers(offset + pageLimit, teacherPageNumber + 1);
  };

  const handlePrevTeacherPageButton = () => {
    setOffsetAndSearchTeachers(offset - pageLimit, teacherPageNumber - 1);
  };

  const isNextTeacherPageButtonDisabled = () => {
    if (teacherPageNumber >= maxPages) {
      return true;
    }
    return false;
  };

  const isPrevTeacherPageButtonDisabled = () => {
    if (teacherPageNumber === 1) {
      return true;
    }
    return false;
  };

  const handleSearchTeachersWithOffsetAndLimit = offset => {
    const requestedTimes = dateTimePreset?.map(({ date, time }) =>
      moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm').unix(),
    );
    const body = {
      prospect_id: prospectID,
      event_name: AC_LED_MAPPING_EVENTS.SEARCH_TEACHERS_FOR_STUDENT,
      event_payload: {
        ...filters,
        requested_times: requestedTimes,
        offset,
        limit: pageLimit,
      },
    };
    searchTeachers(body);
  };

  const handleChange = ({ key, value }) => {
    setFilters(prevFilters => ({ ...prevFilters, [key]: value }));
  };
  const handleSearchTeachers = () => {
    const requestedTimes = dateTimePreset?.map(({ date, time }) =>
      moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm').unix(),
    );
    const body = {
      prospect_id: prospectID,
      event_name: AC_LED_MAPPING_EVENTS.SEARCH_TEACHERS_FOR_STUDENT,
      event_payload: {
        ...filters,
        requested_times: requestedTimes,
      },
    };
    searchTeachers(body);
  };
  const handleGetStudentDetails = newProspectID => {
    if (prospectID === newProspectID) {
      return;
    }
    const body = {
      prospect_id: newProspectID,
      event_name: AC_LED_MAPPING_EVENTS.SEARCH_STUDENT,
      event_payload: {},
    };
    setProspectID(newProspectID);
    fetchStudentDetails(body);
    setSearchResult(null);
    setOffset(0);
    setTeacherPageNumber(1);
    if (mapperType === MAPPER_TYPE.AC) setShowDateTimeFilter(false);
    setDateTimePreset([]);
  };

  const handlePagination = () => setViewNextPage(!viewNextPage);

  const handleOpenAcLedMappingResultFeedbackModal = () => {
    const body = {
      prospect_id: prospectID,
      event_name: AC_LED_MAPPING_EVENTS.REJECT_RECOMMENDED_TEACHERS_CLICK,
      event_payload: {},
    };
    searchResultFeedbackClick(body);
    dispatch(
      openModal(
        MODAL_FORM.AC_LED_MAPPING_RESULT_FEEDBACK_MODAL,
        {
          size: 'custom',
          width: '512px',
          overflow: 'inherit',
        },
        {
          prospectID,
          onClose: () => {
            setShowDateTimeFilter(true);
            setSearchResult(null);
            setOffset(0);
            setTeacherPageNumber(1);
            setDateTimePreset([]);
          },
        },
      ),
    );
  };
  const showStudentDetails = !isStudentDetailsProcessing && studentDetails;

  const showRejectRecommendedTeachersButton =
    !!prospectID && searchResult?.teachers?.length > 0;

  const showTeacherPageButtons = searchResult?.teachers?.length > 0;
  const updateSlot = (slot, id, key, value) =>
    slot?.id === id ? { ...slot, [key]: value } : { ...slot };
  const handleChangeDateTime = ({ key, value, id }) => {
    const updatedDateTimePreset = dateTimePreset?.map(slot =>
      updateSlot(slot, id, key, value),
    );
    setDateTimePreset(updatedDateTimePreset);
  };
  const handleAddNewSlot = () => {
    const newTimeSlot = getNewTimeSlot?.();
    if (!newTimeSlot) {
      return;
    }
    const preset = {
      ...newTimeSlot,
    };
    setDateTimePreset([...dateTimePreset, preset]);
  };

  const handleRemoveSlot = id => {
    const updatedDateTimePreset = dateTimePreset?.filter(slot => slot?.id !== id);
    setDateTimePreset(updatedDateTimePreset);
  };
  const showAddSlotButton = dateTimePreset.length < 3;

  return {
    prospectID,
    handleChange,
    handleGetStudentDetails,
    isSearchProcessing,
    filters,
    handleSearchTeachers,
    studentDetails,
    showStudentDetails,
    handlePagination,
    viewNextPage,
    searchResult,
    handleOpenAcLedMappingResultFeedbackModal,
    showRejectRecommendedTeachersButton,
    handleNextTeacherPageButton,
    handlePrevTeacherPageButton,
    isNextTeacherPageButtonDisabled,
    isPrevTeacherPageButtonDisabled,
    showTeacherPageButtons,
    dateTimePreset,
    handleChangeDateTime,
    handleAddNewSlot,
    handleRemoveSlot,
    showAddSlotButton,
    showDateTimeFilter,
    mappingSearchResult: searchResult,
    mapperType,
  };
};

export default useLogic;
